import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { getUploadData, uploadData } from 'services/api-service/center';
import { ActionCtor } from 'store/modules/main';
import { IStoreState } from '../../../store/modules';
import Accordion from './Accordion';
import FilterPanel from './FilterPanel';

const initialFilters = {
  nameChecked: false,
  name: '',
  countChecked: false,
  count: '0',
  equipmentChecked: false,
  equipment: '',
};

const CenterRescue = () => {
  const dispatch = useDispatch();
  const curRoom = useSelector((store: IStoreState) => store.MainReducer.curRoom);
  const rescue = useSelector((store: IStoreState) => store.MainReducer.rescue);
  const rNumberPhones = useSelector((store: IStoreState) => store.MainReducer.rNumberPhones);
  const [rescues, setRescues] = useState<any>(undefined);
  const fileInput = useRef<HTMLInputElement>(null);

  const onUpload = () => {
    if (fileInput.current !== null && fileInput.current !== undefined) {
      fileInput.current.click();
    }
  };

  const handleUpload = async (e) => {
    try {
      const form = new FormData();
      form.append('excelFile', e.target.files[0]);
      const response: any = await uploadData(form);
      if (response.result === 'success') {
        updateRescue();
        toast('정보가 업데이트되었습니다.', { type: 'success' });
      } else {
        toast(response.error_msg, { type: 'error' });
      }
      e.target.value = '';
    } catch (error) {
      console.log('handleUpload Error ', error);
    }
  };

  const addDataToChildren = (children: any[] | undefined): any[] | undefined => {
    if (!children) return undefined;
    const filtered = children
      .map((child) => {
        if (child.lv === 4) {
          return { ...child, checked: false };
        }
        const updatedChild: any = { ...child, checked: false };
        if (Array.isArray(updatedChild.children) && updatedChild.children.length > 0) {
          updatedChild.children = addDataToChildren(updatedChild.children);
        }
        if (updatedChild.children && Array.isArray(updatedChild.children) && updatedChild.children.length > 0) {
          return updatedChild;
        }
        return undefined;
      })
      .filter(Boolean);
    return filtered.length > 0 ? filtered : undefined;
  };

  const updateRescue = async () => {
    try {
      const response: any = await getUploadData();
      if (response.result === 'success') {
        const list = addDataToChildren(response.dataList);
        setRescues(list);
      }
    } catch (error) {
      console.log('getUploadData Error ', error);
    }
  };

  useEffect(() => {
    if (rescue !== undefined && rescue.length > 0) {
      console.log('CenterRescue rescue : ', rescue);
    }
  }, [rescue]);

  useEffect(() => {
    // initialize
    updateRescue();
  }, []);

  const handleCheckboxChange = (value: string, checked: boolean) => {
    const updateTreeData = (node: any) => {
      if (node.value === value) {
        return { ...node, checked };
      }
      if (node.children) {
        return { ...node, children: node.children.map(updateTreeData) };
      }
      return node;
    };
    setRescues((prevData) => prevData.map(updateTreeData));
  };

  useEffect(() => {
    const extractCheckedItems = (arr: any[]): any[] => {
      const result: any[] = [];
      const extract = (arr: any[]) => {
        arr.forEach((item) => {
          if (item.checked === true && item.lv === undefined) {
            result.push(item.value);
          }
          if (item.children) {
            extract(item.children);
          }
        });
      };
      extract(arr);
      return result;
    };
    if (rescues !== undefined) {
      dispatch(ActionCtor.updateList(extractCheckedItems(rescues)));
    }
  }, [rescues]);

  useEffect(() => {
    if (!rNumberPhones || !rescues) return;

    const syncChecked = (node: any): any => {
      if (node.lv === 4 && Array.isArray(node.children)) {
        return {
          ...node,
          children: node.children.map((member: any) => {
            const phone = (member.value || '').toString().replace(/[^0-9]/g, '');
            return {
              ...member,
              checked: rNumberPhones.includes(phone),
            };
          }),
        };
      }
      if (Array.isArray(node.children)) {
        return { ...node, children: node.children.map(syncChecked) };
      }
      return node;
    };

    setRescues((prev: any) => prev?.map(syncChecked));
  }, [rNumberPhones]);

  useEffect(() => {
    // initialize
    if (curRoom === undefined) {
      setRescues(undefined);
      updateRescue();
    }
  }, [curRoom]);

  const [filters, setFilters] = useState(initialFilters);

  const handleFilterChange = (name: string, value: boolean | string) => {
    setFilters((prev) => {
      if (name === 'nameChecked' && value === false) {
        return { ...prev, nameChecked: false, name: '' };
      }
      if (name === 'countChecked' && value === false) {
        return { ...prev, countChecked: false, count: '0' };
      }
      if (name === 'equipmentChecked' && value === false) {
        return { ...prev, equipmentChecked: false, equipment: '' };
      }
      if (name === 'count' && typeof value === 'string') {
        const numValue = value.replace(/[^0-9]/g, '');
        return { ...prev, [name]: numValue };
      }
      return { ...prev, [name]: value };
    });
  };

  function filterLv4WithHierarchy(node: any, filters: typeof initialFilters): any {
    if (Array.isArray(node)) {
      const filtered = node.map((child) => filterLv4WithHierarchy(child, filters)).filter(Boolean);
      return filtered.length > 0 ? filtered : null;
    }
    if (typeof node !== 'object' || node === null) return null;

    if (node.lv === 4 && Array.isArray(node.children)) {
      const filteredChildren = node.children.filter((member) => {
        if (filters.nameChecked && filters.name && !member.label.includes(filters.name)) return false;
        if (
          filters.countChecked &&
          filters.count &&
          (member.pCnt === undefined || Number(member.pCnt) < Number(filters.count))
        )
          return false;
        if (
          filters.equipmentChecked &&
          filters.equipment &&
          (!member.vesselinfo || !member.vesselinfo.includes(filters.equipment))
        )
          return false;
        return true;
      });
      if (filteredChildren.length > 0) {
        return { ...node, children: filteredChildren };
      }
      return null;
    }

    if (Array.isArray(node.children)) {
      const filteredChildren = node.children.map((child) => filterLv4WithHierarchy(child, filters)).filter(Boolean);
      if (filteredChildren.length > 0) {
        return { ...node, children: filteredChildren };
      }
      return null;
    }

    return node;
  }

  const [appliedFilters, setAppliedFilters] = useState(filters);
  const handleSearch = () => setAppliedFilters(filters);

  // rescues.dataList에서 필터링
  const filteredRescues = useMemo(
    () => (rescues && rescues ? filterRescues(rescues, filters) : undefined),
    [rescues, filters]
  );

  function filterRescues(rescues: any, filters: typeof initialFilters): any[] {
    // const lv4Members = findLv4Children(rescues);
    return filterLv4WithHierarchy(rescues, filters);
  }

  return (
    <CenterRescueTemplateWrap>
      <FilterPanel filters={filters} onChange={handleFilterChange} onSearch={handleSearch} />
      <HeaderText>해양재난구조대</HeaderText>
      <ScrollWrap itemProp={curRoom === undefined ? 'undefined' : 'object'}>
        {filteredRescues === undefined || filteredRescues === null ? (
          <EmptyText>정보가 없습니다.</EmptyText>
        ) : (
          filteredRescues.map((child) => (
            <Accordion key={child.value} node={child} expanded={true} onCheckboxChange={handleCheckboxChange} />
          ))
        )}
      </ScrollWrap>
      {/* {curRoom === undefined && <AddButton onClick={onUpload}>+ 조직도 추가하기</AddButton>} */}
      <input
        type="file"
        ref={fileInput}
        accept=".xlsx, .xls"
        style={{ display: 'none' }}
        onChange={(e) => handleUpload(e)}
      />
    </CenterRescueTemplateWrap>
  );
};

const CenterRescueTemplateWrap = styled.div`
  width: 100%;
  height: 60%;
  text-align: center;
  background: linear-gradient(180deg, #00315c 0%, #000 100%);
`;
const HeaderText = styled.h1`
  height: 40px;
  margin: auto;
  color: #fff;
  text-align: center;
  font-family: 'Noto Sans CJK KR';
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 40px;
  background: linear-gradient(180deg, #0086ff 0%, #005099 100%);
`;
const ScrollWrap = styled.div`
  // position: relative;
  height: calc(100% - 12.125rem);
  // height: ${(props) => (props.itemProp === 'undefined' ? 'calc(100% - 15.625rem)' : 'calc(100% - 12.75rem)')};
  // height: calc(100% - 6.625rem);
  margin: 0.5rem 0;
  overflow: auto;
  ::-webkit-scrollbar {
    display: none;
  }
`;
const EmptyText = styled.h1`
  margin-top: 40px;
  color: #fff;
  font-family: 'Noto Sans CJK KR';
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  text-align: center;
`;
const AddButton = styled.button`
  width: 90%;
  height: 2.25rem;
  color: #fff;
  text-align: center;
  font-family: 'Noto Sans CJK KR';
  font-size: 14px;
  font-size: 1rem;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  border-radius: 0.625rem;
  border: 1px solid #fff;
  background: #375167;
  cursor: pointer;
  z-index: 1;
`;

export default CenterRescue;
